import { json } from "@remix-run/node";
import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import { useOutletContext } from "@remix-run/react";
import type { Locale } from "~/utils/locale";
import { detectLocale } from "~/utils/locale";
import { t } from "~/utils/translations";
import { Breadcrumbs } from "~/components/storefront/Breadcrumbs";

export const loader = async ({ request }: LoaderFunctionArgs) => {
  return json({ locale: detectLocale(request.url) });
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const locale = data?.locale || "vi";
  return [
    { title: locale === "vi" ? "Điều khoản dịch vụ — Diolo" : "Terms of Service — Diolo" },
    { name: "description", content: locale === "vi" ? "Điều khoản dịch vụ Diolo — điều khoản sử dụng, mua hàng và đổi trả." : "Diolo terms of service — terms of use, purchases, and returns." },
  ];
};

export default function TermsPage() {
  const { locale } = useOutletContext<{ locale: Locale }>();

  return (
    <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-8 py-12">
      <Breadcrumbs locale={locale} items={[{ label: t("termsOfService", locale) }]} />
      {/* Header */}
      <div className="text-center mb-16">
        <p className="text-xs uppercase tracking-widest text-luxury-stone mb-4">
          {t("legal", locale)}
        </p>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light text-luxury-charcoal tracking-normal">
          {locale === "vi" ? "Điều khoản dịch vụ" : "Terms of Service"}
        </h1>
        <p className="mt-6 max-w-2xl mx-auto text-luxury-stone text-sm sm:text-base leading-relaxed">
          {locale === "vi"
            ? "Cập nhật lần cuối: Tháng 3, 2026"
            : "Last updated: March 2026"}
        </p>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto space-y-12">
        {/* Terms of Use */}
        <section>
          <h2 className="font-display text-xl sm:text-2xl font-light text-luxury-charcoal tracking-normal mb-4">
            {locale === "vi" ? "Điều khoản sử dụng" : "Terms of Use"}
          </h2>
          <p className="text-luxury-stone text-sm sm:text-base leading-relaxed">
            {locale === "vi"
              ? "Bằng việc truy cập và sử dụng trang web Diolo, bạn đồng ý tuân thủ các điều khoản và điều kiện này. Nếu bạn không đồng ý với bất kỳ phần nào của các điều khoản này, vui lòng không sử dụng trang web."
              : "By accessing and using the Diolo website, you agree to comply with these terms and conditions. If you do not agree with any part of these terms, please do not use the website."}
          </p>
        </section>

        {/* Purchases */}
        <section>
          <h2 className="font-display text-xl sm:text-2xl font-light text-luxury-charcoal tracking-normal mb-4">
            {locale === "vi" ? "Mua hàng" : "Purchases"}
          </h2>
          <p className="text-luxury-stone text-sm sm:text-base leading-relaxed">
            {locale === "vi"
              ? "Tất cả đơn hàng đều phụ thuộc vào tình trạng còn hàng. Giá sản phẩm có thể thay đổi mà không cần thông báo trước. Chúng tôi có quyền từ chối hoặc hủy bất kỳ đơn hàng nào vì bất kỳ lý do gì."
              : "All orders are subject to product availability. Prices are subject to change without notice. We reserve the right to refuse or cancel any order for any reason."}
          </p>
        </section>

        {/* Payment */}
        <section>
          <h2 className="font-display text-xl sm:text-2xl font-light text-luxury-charcoal tracking-normal mb-4">
            {locale === "vi" ? "Thanh toán" : "Payment"}
          </h2>
          <p className="text-luxury-stone text-sm sm:text-base leading-relaxed">
            {locale === "vi"
              ? "Chúng tôi chấp nhận các phương thức thanh toán được liệt kê trên trang thanh toán. Thanh toán được xử lý an toàn qua các nhà cung cấp thanh toán uy tín."
              : "We accept the payment methods listed on our checkout page. Payments are processed securely through trusted payment providers."}
          </p>
        </section>

        {/* Shipping */}
        <section>
          <h2 className="font-display text-xl sm:text-2xl font-light text-luxury-charcoal tracking-normal mb-4">
            {locale === "vi" ? "Vận chuyển" : "Shipping"}
          </h2>
          <p className="text-luxury-stone text-sm sm:text-base leading-relaxed">
            {locale === "vi"
              ? "Thời gian giao hàng là ước tính và có thể thay đổi. Chúng tôi không chịu trách nhiệm về sự chậm trễ do đơn vị vận chuyển hoặc hải quan gây ra."
              : "Delivery times are estimates and may vary. We are not responsible for delays caused by shipping carriers or customs processing."}
          </p>
        </section>

        {/* Returns & Refunds */}
        <section>
          <h2 className="font-display text-xl sm:text-2xl font-light text-luxury-charcoal tracking-normal mb-4">
            {locale === "vi" ? "Đổi trả & Hoàn tiền" : "Returns & Refunds"}
          </h2>
          <p className="text-luxury-stone text-sm sm:text-base leading-relaxed">
            {locale === "vi"
              ? "Sản phẩm có thể được đổi trả trong vòng 30 ngày kể từ ngày nhận hàng. Sản phẩm phải còn nguyên tem mác, chưa qua sử dụng và trong bao bì gốc. Phí vận chuyển đổi trả do khách hàng chịu, trừ trường hợp sản phẩm bị lỗi."
              : "Products may be returned within 30 days of receipt. Items must have original tags attached, be unworn, and in original packaging. Return shipping costs are the responsibility of the customer unless the item is defective."}
          </p>
        </section>

        {/* Intellectual Property */}
        <section>
          <h2 className="font-display text-xl sm:text-2xl font-light text-luxury-charcoal tracking-normal mb-4">
            {locale === "vi" ? "Sở hữu trí tuệ" : "Intellectual Property"}
          </h2>
          <p className="text-luxury-stone text-sm sm:text-base leading-relaxed">
            {locale === "vi"
              ? "Tất cả nội dung trên trang web này, bao gồm hình ảnh, văn bản, logo và thiết kế, đều thuộc sở hữu của Diolo và được bảo vệ bởi luật sở hữu trí tuệ."
              : "All content on this website, including images, text, logos, and designs, is the property of Diolo and is protected by intellectual property laws."}
          </p>
        </section>

        {/* Limitation of Liability */}
        <section>
          <h2 className="font-display text-xl sm:text-2xl font-light text-luxury-charcoal tracking-normal mb-4">
            {locale === "vi" ? "Giới hạn trách nhiệm" : "Limitation of Liability"}
          </h2>
          <p className="text-luxury-stone text-sm sm:text-base leading-relaxed">
            {locale === "vi"
              ? "Diolo không chịu trách nhiệm về bất kỳ thiệt hại gián tiếp, ngẫu nhiên hoặc do hậu quả nào phát sinh từ việc sử dụng trang web hoặc sản phẩm của chúng tôi."
              : "Diolo shall not be liable for any indirect, incidental, or consequential damages arising from the use of our website or products."}
          </p>
        </section>

        {/* Contact */}
        <section>
          <h2 className="font-display text-xl sm:text-2xl font-light text-luxury-charcoal tracking-normal mb-4">
            {t("contactShort", locale)}
          </h2>
          <p className="text-luxury-stone text-sm sm:text-base leading-relaxed">
            {locale === "vi"
              ? "Nếu bạn có câu hỏi về các điều khoản này, vui lòng liên hệ chúng tôi tại hello@diolo.com."
              : "If you have questions about these terms, please contact us at hello@diolo.com."}
          </p>
        </section>
      </div>
    </div>
  );
}

export { RouteErrorBoundary as ErrorBoundary } from "~/components/RouteErrorBoundary";
